<template>
    <div class="sdev-auth-body" :class="{ 'is-entering': entrancePhase }">
        <div class="sdev-auth-grid" aria-hidden="true"></div>
        <div class="sdev-auth-horizon" aria-hidden="true"></div>

        <section v-if="!entrancePhase" class="sdev-auth-stage">
            <div class="sdev-auth-manifest">
                <div class="sdev-auth-mark" aria-hidden="true"><span></span><span></span><span></span></div>
                <p class="sdev-auth-kicker">SDEV · AUTHORITATIVE CONTROL PLANE</p>
                <h1>Welcome to <em>Bedrock</em></h1>
                <p class="sdev-auth-lead">It's not our mission to change the past.<br>But we can build a simpler future.</p>
                <div class="sdev-auth-principles">
                    <span>Canonical truth</span>
                    <span>Explicit authority</span>
                    <span>Auditable change</span>
                </div>
            </div>

            <form class="sdev-auth-panel" autocomplete="off" @submit.prevent="handleLogin">
                <header>
                    <small>IDENTITY VERIFICATION</small>
                    <h2>Bedrock betreten</h2>
                    <p>Authentifiziere deine Identität, um die Control Plane zu öffnen.</p>
                </header>

                <label>
                    <span>Identity Key</span>
                    <input v-model.trim="identityKey" autocomplete="username" required autofocus>
                </label>
                <label>
                    <span>Passwort</span>
                    <input v-model="password" type="password" autocomplete="current-password" required>
                </label>

                <p v-if="error" class="sdev-auth-error">{{ error }}</p>

                <button type="submit" :disabled="isLoading">
                    <span>{{ isLoading ? "Identität wird geprüft …" : "Enter Bedrock" }}</span>
                    <i aria-hidden="true">→</i>
                </button>
                <small class="sdev-auth-security">Geschützte Sitzung · Ereignisse werden revisionssicher protokolliert</small>
            </form>
        </section>

        <section v-else class="sdev-bedrock-entrance" :class="`phase-${entrancePhase}`" aria-live="polite">
            <div class="sdev-entrance-orbit" aria-hidden="true"><i></i><i></i><i></i></div>
            <div class="sdev-entrance-core" aria-hidden="true"><span>B</span></div>
            <p class="sdev-entrance-kicker">{{ entranceKicker }}</p>
            <h2>{{ entranceTitle }}</h2>
            <p>{{ entranceMessage }}</p>
            <div class="sdev-entrance-line" aria-hidden="true"><i></i><span></span><i></i></div>
        </section>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue"
import { useRoute, useRouter } from "vue-router"
import { activateAuthenticatedSession, login } from "@/domain/auth/auth.service"
import { authState } from "@/domain/auth/auth.state"
import { initializeNavigation } from "@/domain/navigation"
import { systemBoot } from "@/domain/system/system.boot"

const route = useRoute()
const router = useRouter()
const identityKey = ref("")
const password = ref("")
const error = ref("")
const isLoading = ref(false)
const entrancePhase = ref("")

const entranceContent = {
    verified: {
        kicker: "IDENTITY ACCEPTED",
        title: "Authority confirmed.",
        message: "Deine Identität wurde durch Bedrock verifiziert."
    },
    linking: {
        kicker: "GRAPH CONTEXT ESTABLISHED",
        title: "The foundation responds.",
        message: "Identität, Capabilities und Control Plane werden verbunden."
    },
    ready: {
        kicker: "SDEV BEDROCK · READY",
        title: "Enter Bedrock.",
        message: "Die autoritative Ebene ist bereit."
    }
}

const entranceKicker = computed(() => entranceContent[entrancePhase.value]?.kicker || "")
const entranceTitle = computed(() => entranceContent[entrancePhase.value]?.title || "")
const entranceMessage = computed(() => entranceContent[entrancePhase.value]?.message || "")

function wait(milliseconds) {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return new Promise(resolve => window.setTimeout(resolve, Math.min(milliseconds, 100)))
    }
    return new Promise(resolve => window.setTimeout(resolve, milliseconds))
}

async function handleLogin() {
    error.value = ""
    isLoading.value = true

    try {
        await login(identityKey.value, password.value, { deferActivation: true })
        entrancePhase.value = "verified"
        await Promise.all([initializeNavigation(), wait(520)])
        entrancePhase.value = "linking"
        await wait(720)
        entrancePhase.value = "ready"
        await wait(620)

        activateAuthenticatedSession()
        systemBoot.setPhase("ready")
        systemBoot.ready = true

        await router.replace({ path: route.query.redirect || "/instances", query: {} })
    } catch (exception) {
        const status = exception?.response?.status
        entrancePhase.value = ""
        error.value = status === 401
            ? "Identity Key oder Passwort sind nicht korrekt."
            : "Bedrock konnte die Identität nicht verifizieren. Bitte erneut versuchen."
        isLoading.value = false
    }
}

onMounted(() => {
    authState.isBootingSystem = false
    authState.bootPhase = null
})
</script>
